import React, { Component } from 'react';
import { Box, Card, CardContent, Chip, CircularProgress, Collapse, IconButton, Pagination, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ReactJson from 'react-json-view';
import Api from '../../services/api';
import UsageService from '../../services/usage.service';
import { UsageEmpty, UsageBlockTitle, fmtDateTime, fmtInt } from './usage-widgets';
import { t } from './usage-i18n';

// ---------------------------------------------------------------------------
// Bloco "Escritas": trilha do outbox (tb_calleds_outbox ∪ _archive) da entidade consultada na aba
// "Quem acessou". Grid com quando / quem / entidade / o que foi feito, e o botão "+" que abre o
// de-para (changed_fields = {campo: {old, new}}) ou o event_data quando não há de-para (criação).
// Carrega sozinho ao montar; o pai troca a `key` para recarregar com outra entidade/período.
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;
// O que o outbox registra (aggregate_type): CALLED, CHAT (+ CHAT_MESSAGE), USER, ORGANIZATION, CATEGORY, TEAM.
export const WRITE_ENTITIES = ['called', 'chat', 'user', 'organization', 'organization_category', 'team'];

// Rótulo por código de evento; código desconhecido cai no próprio código (nunca esconde).
const CODE_FALLBACK = {
  'called.created': 'Chamado criado', 'called.updated': 'Chamado alterado', 'called.closed': 'Chamado encerrado',
  'called.status_changed': 'Status alterado', 'called.priority_changed': 'Prioridade alterada', 'called.type_changed': 'Tipo alterado',
  'called.agent_assigned': 'Agente atribuído', 'called.accompaniment_added': 'Acompanhamento adicionado',
  'called.attachment_added': 'Anexo adicionado', 'called.tag_added': 'Tag adicionada', 'called.custom_field_changed': 'Campo customizado alterado',
  'called.rated': 'Chamado avaliado',
  'chat.created': 'Chat criado', 'chat.closed': 'Chat encerrado', 'chat.agent_joined': 'Agente entrou no chat',
  'chat.message_added': 'Mensagem adicionada', 'chat.tag_added': 'Tag adicionada ao chat',
  'user.created': 'Usuário criado', 'user.updated': 'Usuário alterado', 'user.tag_added': 'Tag adicionada ao usuário',
  'organization.created': 'Organização criada', 'category.created': 'Categoria criada',
  'team.created': 'Equipe criada', 'team.user_associated': 'Usuário associado à equipe',
  'team.organization_associated': 'Organização associada à equipe', 'team.category_associated': 'Categoria associada à equipe',
};

const ACTOR_COLORS = { USER: '#009688', CUSTOMER: '#3F51B4', SYSTEM: '#607D8B' };

export const writeCodeLabel = (intl, ev) => {
  const code = ev.eventTypeCode || (ev.eventType ? ev.eventType.toLowerCase() : '');
  if (!code) return '—';
  return t(intl, `usage.write.code.${code}`, CODE_FALLBACK[code] || code);
};

const fmtVal = (v) => {
  if (v === null || v === undefined) return '∅';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

// Resumo curto a partir do event_data (status previous → current, título...), sem inventar campos.
const summaryOf = (ev) => {
  const d = ev.eventData;
  if (!d || typeof d !== 'object') return '';
  const parts = [];
  ['status', 'priority', 'type', 'assigned_user', 'agent'].forEach((k) => {
    const v = d[k];
    if (v && typeof v === 'object' && ('previous' in v || 'current' in v)) parts.push(`${k}: ${fmtVal(v.previous)} → ${fmtVal(v.current)}`);
  });
  if (d.description && typeof d.description === 'string') parts.push(d.description.slice(0, 80));
  if (d.title && typeof d.title === 'string' && parts.length === 0) parts.push(d.title.slice(0, 80));
  return parts.join(' · ');
};

// Entidade da linha: o agregado (called #6900) ou o filho quando o código diz (attachment, accompaniment...).
const entityOf = (ev) => {
  const code = ev.eventTypeCode || '';
  const child = code.match(/\.(attachment|accompaniment|tag|custom_field|message|user|organization|category)_/);
  const agg = (ev.aggregateType || '').toLowerCase();
  const d = ev.eventData && typeof ev.eventData === 'object' ? ev.eventData : {};
  if (agg === 'chat_message') return `message #${ev.aggregateId || '?'}${d.chat_id ? ` · chat #${d.chat_id}` : ''}`;
  if (child) return `${child[1]}${ev.aggregateId ? ` #${ev.aggregateId}` : ''}${ev.calledId && agg === 'called' ? ` · called #${ev.calledId}` : ''}${agg === 'team' && ev.aggregateId ? ` · team #${ev.aggregateId}` : ''}`;
  return `${agg || '—'}${ev.aggregateId ? ` #${ev.aggregateId}` : ev.calledId ? ` #${ev.calledId}` : ''}`;
};

export default class UsageWriteEvents extends Component {
  constructor(props) {
    super(props);
    this.state = { loading: true, rows: [], count: 0, page: 1, open: {}, error: null };
    this.onPage = this.onPage.bind(this);
  }

  componentDidMount() { this.load(1); }

  tr(id, fallback) { return t(this.props.intl, id, fallback); }
  onPage(e, page) { this.load(page); }
  toggle(id) { this.setState((s) => ({ open: { ...s.open, [id]: !s.open[id] } })); }

  load(page) {
    const { entityType, entityId, dateStart, dateEnd } = this.props;
    this.setState({ loading: true, page, open: {} });
    UsageService.entityWrites(entityType, entityId, dateStart, dateEnd, page, PAGE_SIZE)
      .then((r) => this.setState({ loading: false, rows: r.data, count: r.count }))
      .catch((err) => { Api.kickoff(err); this.setState({ loading: false, error: err.message }); });
  }

  renderDiff(ev) {
    const { intl } = this.props;
    const cf = ev.changedFields && typeof ev.changedFields === 'object' ? ev.changedFields : null;
    const keys = cf ? Object.keys(cf) : [];
    if (keys.length > 0) {
      return (
        <Table size="small" style={{ background: '#fafafa' }}>
          <TableHead><TableRow>
            <TableCell>{this.tr('usage.write.col.field', 'Campo')}</TableCell><TableCell>{this.tr('usage.write.col.before', 'Antes')}</TableCell><TableCell>{this.tr('usage.write.col.after', 'Depois')}</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {keys.map((k) => {
              const v = cf[k] || {};
              const has = typeof v === 'object' && v !== null && ('old' in v || 'new' in v);
              return (
                <TableRow key={k}>
                  <TableCell style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{k}</TableCell>
                  <TableCell style={{ color: '#c62828', wordBreak: 'break-word' }}>{has ? fmtVal(v.old) : (ev.oldValues && ev.oldValues[k] !== undefined ? fmtVal(ev.oldValues[k]) : '∅')}</TableCell>
                  <TableCell style={{ color: '#2e7d32', wordBreak: 'break-word' }}>{has ? fmtVal(v.new) : fmtVal(v)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      );
    }
    if (ev.eventData && typeof ev.eventData === 'object') {
      return (
        <Box>
          <Typography variant="caption" style={{ color: '#888' }}>{this.tr('usage.write.noDiff', 'Sem de-para (evento de criação ou de anexo): conteúdo do evento')}</Typography>
          <Box style={{ backgroundColor: '#1a1a2e', color: '#e0e0e0', borderRadius: 8, padding: 8, marginTop: 4, overflowX: 'auto' }}>
            <ReactJson src={ev.eventData} theme="monokai" name={false} collapsed={2} displayDataTypes={false} displayObjectSize={false} enableClipboard={false} style={{ background: 'transparent', fontSize: 12 }} />
          </Box>
        </Box>
      );
    }
    return <Typography variant="caption" style={{ color: '#888' }}>{this.tr('usage.write.empty', 'Evento sem detalhe registrado.')}</Typography>;
  }

  render() {
    const { intl } = this.props;
    const { loading, rows, count, page, open, error } = this.state;
    const actorLabel = (ev) => {
      if (ev.actorType === 'SYSTEM' || (!ev.actorId && !ev.actorName)) return this.tr('usage.write.actor.system', 'sistema');
      if (ev.actorName) return ev.actorName;
      return ev.actorId ? `#${ev.actorId}` : this.tr('usage.write.actor.system', 'sistema');
    };
    return (
      <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12, borderLeft: '4px solid #8BC34A', marginTop: 12 }}>
        <CardContent style={{ paddingBottom: 8 }}>
          <UsageBlockTitle title={this.tr('usage.write.title', 'Escritas')} subtitle={`${fmtInt(count)} ${this.tr('usage.write.subtitle', 'alteração(ões) registradas pelo outbox no período · "+" abre o de-para')}`} />
          <Typography variant="caption" style={{ color: '#888', display: 'block', marginBottom: 6 }}>{this.tr('usage.write.timeNote', 'Hora do servidor de banco (outbox), pode diferir da hora local dos acessos acima.')}</Typography>
          {loading && <Box display="flex" justifyContent="center" style={{ padding: 12 }}><CircularProgress size={22} color="secondary" /></Box>}
          {!loading && error && <Typography variant="body2" style={{ color: '#c62828' }}>{error}</Typography>}
          {!loading && !error && rows.length === 0 && <UsageEmpty intl={intl} compact title={this.tr('usage.write.none', 'Nenhuma escrita registrada no período')} />}
          {!loading && rows.length > 0 && (
            <Box style={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell style={{ width: 40 }} />
                  <TableCell>{this.tr('usage.col.when', 'Quando')}</TableCell><TableCell>{this.tr('usage.col.user', 'Usuário')}</TableCell>
                  <TableCell>{this.tr('usage.col.entity', 'Entidade')}</TableCell><TableCell>{this.tr('usage.write.col.what', 'O que foi feito')}</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {rows.map((ev) => {
                    const isOpen = !!open[ev.id];
                    const fields = ev.changedFields && typeof ev.changedFields === 'object' ? Object.keys(ev.changedFields).length : 0;
                    return (
                      <React.Fragment key={ev.id}>
                        <TableRow hover style={{ cursor: 'pointer' }} onClick={() => this.toggle(ev.id)}>
                          <TableCell><IconButton size="small" onClick={(e) => { e.stopPropagation(); this.toggle(ev.id); }}>{isOpen ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}</IconButton></TableCell>
                          <TableCell style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDateTime(ev.occurredAt)}</TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                              <span>{actorLabel(ev)}</span>
                              {ev.actorType && <Chip size="small" label={ev.actorType.toLowerCase()} style={{ height: 18, fontSize: 10, background: ACTOR_COLORS[ev.actorType] || '#9E9E9E', color: '#fff' }} />}
                            </Box>
                          </TableCell>
                          <TableCell style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{entityOf(ev)}</TableCell>
                          <TableCell>
                            <Tooltip title={ev.eventTypeCode || ev.eventType || ''}><span style={{ fontWeight: 600 }}>{writeCodeLabel(intl, ev)}</span></Tooltip>
                            {fields > 0 && <Chip size="small" variant="outlined" label={`${fields} ${this.tr('usage.write.fields', 'campo(s)')}`} style={{ marginLeft: 8, height: 18, fontSize: 10 }} />}
                            {summaryOf(ev) && <Typography variant="caption" display="block" style={{ color: '#666' }}>{summaryOf(ev)}</Typography>}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={5} style={{ padding: 0, borderBottom: isOpen ? undefined : 'none' }}>
                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                              <Box style={{ padding: '8px 16px 12px 56px' }}>{this.renderDiff(ev)}</Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
          {!loading && count > PAGE_SIZE && <Box style={{ marginTop: 8 }}><Pagination count={Math.ceil(count / PAGE_SIZE)} page={page} onChange={this.onPage} /></Box>}
        </CardContent>
      </Card>
    );
  }
}
